import type { Artifact, AskedQuestion, ConversationAgentState, ConversationDetail, ConversationMessage } from '@deskworks/shared-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowUp, Bot, Check, Ear, Loader2, Mic, Moon, RotateCcw, Sparkles, Square, User } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { ChatAction } from '@/components/actions/actions';
import { readMarks, ReadMark, type Mark } from '@/components/chat/read-mark';
import { HandoffCard, isHandoff, isTrainingNotice, isTrainingProposal, TrainingNotice, TrainingProposalCard } from '@/components/chat/training-cards';
import { VickiActionCard } from '@/components/front-desk/vicki-action-card';
import { DriveNotice, isDriveNotice } from '@/components/front-desk/vicki-drive';
import {
  askedOf,
  AskedTo,
  isTalkAsked,
  isOnRoundCard,
  isTalkNotice,
  roundCover,
  roundRows,
  TalkNotice,
  TalkRoundCard,
  talkRequest,
  useChatQuestions,
  useMentionPicker,
  type Mentions,
  type TalkActivity,
} from '@/components/chat/desk-talk';
import { isOpenInChat, WorkItemCard, workItemMeta, workItemsOfChat, WorkItemsTray, type ChatWorkItem } from '@/components/chat/work-item-cards';
import { AgentSteps, currentTurn } from '@/components/agent-steps';
import { Markdown } from '@/components/markdown';
import { AttachButton, AttachmentChips, DropZone, MessageAttachments, type Attachments } from '@/components/resources/resources';
import { artifactFile, FilePreview } from '@/components/files/file-preview';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { api, errorMessage } from '@/lib/api';
import { t } from '@/lib/i18n';
import { formatAbsolute, formatTime } from '@/lib/format';
import { qk } from '@/lib/query-keys';
import { useRealtimeEnvelope, useRealtimeStatus } from '@/lib/realtime';
import { useToast } from '@/lib/toast';
import { useAutoScroll } from '@/lib/use-auto-scroll';
import { useChatInput } from '@/lib/use-chat-input';
import { useDictation } from '@/lib/use-dictation';
import { cn } from '@/lib/utils';
import { SessionLink } from '@/components/session-link';

// ---------------------------------------------------------------- data

export function appendMessage(old: ConversationDetail | undefined, msg: ConversationMessage): ConversationDetail | undefined {
  if (!old || old.messages.some((m) => m.id === msg.id)) return old;
  return {
    ...old,
    messages: [...old.messages, msg].sort((a, b) => a.seq - b.seq),
    lastMessagePreview: msg.content.slice(0, 200),
    lastMessageAt: msg.createdAt,
  };
}

export interface ConversationLive {
  /** Transient "what the agent is doing" line from realtime (not persisted). */
  activity: string | null;
  /** Optimistic: a message was just sent and the server hasn't reported the agent as busy yet. */
  awaitingReply: boolean;
  markSent: () => void;
}

/**
 * Loads a conversation and keeps it live: realtime messages are appended directly, `conversation.updated`
 * activity lines are kept in state, and while the socket is down a busy agent is polled every 3 s.
 */
export function useConversationLive(conversationId: string | null) {
  const queryClient = useQueryClient();
  const realtime = useRealtimeStatus();
  const [activity, setActivity] = useState<string | null>(null);
  const [awaitingReply, setAwaitingReply] = useState(false);

  // Reset transient state when switching conversations.
  useEffect(() => {
    setActivity(null);
    setAwaitingReply(false);
  }, [conversationId]);

  const query = useQuery({
    queryKey: qk.conversation(conversationId ?? ''),
    queryFn: () => api.conversations.get(conversationId!),
    enabled: !!conversationId,
    refetchInterval: (q) => {
      const s = q.state.data?.agentState;
      return realtime !== 'connected' && (s === 'starting' || s === 'thinking' || awaitingReply) ? 3000 : false;
    },
  });

  useRealtimeEnvelope((env) => {
    if (!conversationId) return;
    const data = (env.data ?? {}) as { conversationId?: string; activity?: string };
    if (data.conversationId !== conversationId) return;
    if (env.event === 'conversation.updated' && data.activity) setActivity(data.activity);
    if (env.event === 'conversation.message') {
      const msg = env.data as ConversationMessage;
      if (msg?.id && typeof msg.content === 'string') {
        queryClient.setQueryData<ConversationDetail>(qk.conversation(conversationId), (old) => appendMessage(old, msg));
        if (msg.senderType !== 'user') {
          setActivity(null);
          setAwaitingReply(false);
        }
        // A work item card may change the desk's open items.
        if ((msg.metadata as { workItemId?: string } | undefined)?.workItemId) {
          void queryClient.invalidateQueries({ queryKey: ['desk'] });
        }
      }
    }
  });

  const state = query.data?.agentState;
  // The server has caught up (or settled) — drop the optimistic state and stale activity.
  useEffect(() => {
    if (state === 'starting' || state === 'thinking') setAwaitingReply(false);
    if (state === 'listening' || state === 'sleeping' || state === 'failed') setActivity(null);
  }, [state]);
  useEffect(() => {
    if (!awaitingReply) return;
    const timer = window.setTimeout(() => setAwaitingReply(false), 20_000);
    return () => window.clearTimeout(timer);
  }, [awaitingReply]);

  const live: ConversationLive = { activity, awaitingReply, markSent: () => setAwaitingReply(true) };
  return { query, live };
}

/** State to display: right after sending, show "wakes up"/"thinking" until the server reports it. */
export function effectiveAgentState(conv: ConversationDetail | null, awaitingReply: boolean): ConversationAgentState | null {
  if (!conv) return awaitingReply ? 'starting' : null;
  if (awaitingReply && (conv.agentState === 'sleeping' || conv.agentState === 'listening')) {
    return conv.agentState === 'sleeping' ? 'starting' : 'thinking';
  }
  return conv.agentState;
}

/** Last system message, if the agent is in `failed` state. */
export function failedMessage(conv: ConversationDetail | null): ConversationMessage | undefined {
  if (!conv || conv.agentState !== 'failed') return undefined;
  return [...conv.messages].reverse().find((m) => m.senderType === 'system' && !isWorkItemMessage(m));
}

/**
 * Heuristic: did the agent fail because its model isn't connected (missing/rejected key or login)?
 * Matches the English backend texts ("The OpenAI API key was rejected.", "No Anthropic API key is set …",
 * "No Claude login is set …") and, as a fallback for older stored messages, the former German ones.
 */
export function isCredentialError(text: string | undefined): boolean {
  if (!text) return false;
  const lower = text.toLowerCase();
  const english =
    (/api[\s-]?key/.test(lower) && /rejected|not set|\bno\b/.test(lower)) ||
    (/\blogin\b/.test(lower) && /not set|\bno\b|rejected|expired/.test(lower)) ||
    /unauthori[sz]ed|invalid[_ ]api[_ ]key|\b401\b/.test(lower);
  const germanFallback = /api[- ]?(key|schlüssel)|schlüssel|zugangsdaten|anmeld|nicht verbunden|abgelehnt/.test(lower);
  return english || germanFallback;
}

// ---------------------------------------------------------------- thread

interface PendingMessage {
  tempId: string;
  content: string;
  createdAt: string;
  failed?: boolean;
  /** Files sent with it (docs/features/resources.md). */
  resources?: { id: string; name: string }[];
  /** The agent's message whose offered answer it is. */
  answers?: string;
}

/** Sent when only files are attached. */
const FILES_ONLY = 'Here are the attached files.';

/**
 * Messages + agent status line + composer for one conversation. With `conv === null` it shows `emptyState`
 * and the first message is sent through `onCreate` (which creates the conversation).
 */
export function ConversationThread({
  conv,
  agentName,
  workspaceId,
  live,
  onCreate,
  intro,
  emptyState,
  footer,
  banner,
  placeholder,
  disabledNotice,
  className,
  autoFocus,
  attachments,
  stepsUnderReplies,
  mentions,
  composerExtra,
}: {
  conv: ConversationDetail | null;
  agentName: string;
  workspaceId: string;
  live: ConversationLive;
  onCreate?: (content: string) => Promise<unknown>;
  intro?: ReactNode;
  emptyState?: ReactNode;
  footer?: ReactNode;
  /** Shown right above the composer (e.g. model onboarding). */
  banner?: ReactNode;
  placeholder?: string;
  /** When set, the composer is replaced by this notice (e.g. archived). */
  disabledNotice?: ReactNode;
  className?: string;
  autoFocus?: boolean;
  /** Files can be attached to messages (setup chats, docs/features/resources.md). */
  attachments?: Attachments;
  /** Every reply shows the agent's steps that led to it, open (the training, docs/features/desk-training.md). */
  stepsUnderReplies?: boolean;
  /** Desks (and in a chat between desks, agents) a person asks with @ (docs/features/desk-talk.md). */
  mentions?: Mentions;
  /** More in the field's row, left of the send button (e.g. talking with VICKI). */
  composerExtra?: ReactNode;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [pending, setPending] = useState<PendingMessage[]>([]);
  const [draft, setDraft] = useState('');
  const input = useChatInput(draft, () => submit());
  const picker = useMentionPicker(draft, setDraft, input.ref, mentions);
  const dictation = useDictation(draft, setDraft, (msg) => toast.error(t('Dictation'), msg));

  const state = effectiveAgentState(conv, live.awaitingReply || (!conv && pending.some((p) => !p.failed)));
  const failed = failedMessage(conv);
  const messages = conv?.messages ?? [];
  // Work Items accepted here: each one's live card, and above the composer the ones not done yet (newest first).
  const workItems = useMemo(() => workItemsOfChat(messages), [messages]);
  const openWorkItems = [...workItems.values()].filter(isOpenInChat).map((i) => i.id).reverse();
  // Desks asked from here (docs/features/desk-talk.md#where-it-shows): each round of questions is one card that
  // follows them live and carries their answers.
  const asksDesks = useMemo(() => messages.some(isTalkAsked), [messages]);
  const talk = useChatQuestions(conv?.id ?? '', !!conv && asksDesks);
  const questions = useMemo(() => new Map(talk.questions.map((q) => [q.id, q])), [talk.questions]);
  const cover = useMemo(() => roundCover(messages), [messages]);
  const rounds = useMemo(() => messages.filter(isTalkAsked).map((m) => ({ message: m, rows: roundRows(m, questions) })), [messages, questions]);
  const asked = rounds.flatMap((r) => r.rows);
  const openRounds = rounds.filter((r) => r.rows.some((x) => x.q.status === 'open'));
  const done = {
    n: workItems.size - openWorkItems.length + asked.filter((x) => x.q.status !== 'open').length,
    of: workItems.size + asked.length,
  };
  const scrollRef = useAutoScroll<HTMLDivElement>(`${messages.length}:${pending.length}:${state}:${live.activity ?? ''}`);

  const send = async (content: string, retryOf?: string, resources?: { id: string; name: string }[], answers?: string) => {
    const tempId = retryOf ?? `temp-${Date.now()}`;
    setPending((p) =>
      retryOf
        ? p.map((m) => (m.tempId === retryOf ? { ...m, failed: false } : m))
        : [...p, { tempId, content, createdAt: new Date().toISOString(), resources, answers }],
    );
    try {
      // A message that asks desks with @ (docs/features/desk-talk.md); a clicked answer goes to the chat's agent.
      const talk = conv && mentions && !resources?.length && !answers ? talkRequest(content, mentions) : null;
      if (conv && talk) {
        const sent = await api.conversations.talk(conv.id, talk);
        if (sent.message) queryClient.setQueryData<ConversationDetail>(qk.conversation(conv.id), (old) => appendMessage(old, sent.message!));
        void queryClient.invalidateQueries({ queryKey: qk.conversation(conv.id) });
      } else if (conv) {
        const msg = await api.conversations.send(conv.id, content, resources?.map((r) => r.id), answers);
        queryClient.setQueryData<ConversationDetail>(qk.conversation(conv.id), (old) => appendMessage(old, msg));
        void queryClient.invalidateQueries({ queryKey: qk.conversation(conv.id) });
        void queryClient.invalidateQueries({ queryKey: conv.deskId ? qk.deskConversations(conv.deskId) : qk.setupConversations(conv.workspaceId) });
      } else if (onCreate) {
        await onCreate(content);
      }
      setPending((p) => p.filter((m) => m.tempId !== tempId));
      live.markSent();
    } catch (err) {
      setPending((p) => p.map((m) => (m.tempId === tempId ? { ...m, failed: true } : m)));
      toast.error(t('Message not sent'), errorMessage(err));
    }
  };

  const working = state === 'starting' || state === 'thinking';
  // What the agent at work read of what the person wrote since its last reply.
  const marks = working ? readMarks(messages, conv?.execution, 'reply') : null;
  // Which turn of its run each reply ended (1st, 2nd … reply of that execution), to show the steps that led to it.
  const replyTurns = stepsUnderReplies ? turnsOfReplies(messages) : null;

  // Answers the agent offers in its replies (docs/features/conversations.md): they stay in the reply, the one picked is
  // marked, and another one can be picked later — also in an older reply.
  const choices = new Map(useMemo(() => chosenAnswers(messages), [messages]));
  for (const p of pending) if (p.answers && !p.failed) choices.set(p.answers, p.content);
  const pick = disabledNotice ? undefined : (m: ConversationMessage, answer: string) => void send(answer, undefined, undefined, m.id);

  const files = attachments?.items.flatMap((a) => (a.id ? [{ id: a.id, name: a.name }] : [])) ?? [];
  const canSend = (!!draft.trim() || files.length > 0) && !attachments?.uploading;

  const submit = () => {
    const content = draft.trim() || (files.length ? FILES_ONLY : '');
    if (!canSend || !content || disabledNotice) return;
    dictation.stop(true);
    setDraft('');
    attachments?.clear();
    void send(content, undefined, files.length ? files : undefined);
    input.ref.current?.focus();
  };


  const thread = (
    <div className={cn('flex min-h-0 flex-col', attachments ? 'flex-1' : className)}>
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto flex max-w-3xl flex-col gap-4 px-3 py-4 sm:px-6 sm:py-5">
          {intro}
          {messages.length === 0 && pending.length === 0 && emptyState}
          {messages.filter((m) => !isOnRoundCard(m, cover)).map((m) => (
            <MessageItem
              key={m.id}
              message={m}
              agentName={agentName}
              workspaceId={workspaceId}
              deskId={conv?.deskId ?? null}
              workItem={workItems.get(workItemMeta(m)?.workItemId ?? '')}
              questions={questions}
              talkActivity={talk.activity}
              error={m === failed}
              replyTurn={replyTurns?.get(m.id)}
              mark={marks?.get(m.id)}
              choice={choices.get(m.id)}
              onPick={pick}
            />
          ))}
          {pending.map((p) => (
            <UserBubble key={p.tempId} content={p.content} createdAt={p.createdAt} pending={!p.failed}>
              {p.resources && <MessageAttachments resources={p.resources} />}
              {p.failed && (
                <button
                  type="button"
                  onClick={() => void send(p.content, p.tempId, p.resources, p.answers)}
                  className="mt-1 inline-flex cursor-pointer items-center gap-1 text-xs text-destructive hover:underline"
                >
                  <RotateCcw className="size-3" /> {t('Not sent — send again')}
                </button>
              )}
            </UserBubble>
          ))}
          {state && !disabledNotice && <AgentStatusLine state={state} activity={live.activity} agentName={agentName} failedText={failed?.content} />}
          {conv?.execution && stepsUnderReplies ? (
            // The replies carry their steps; here only the turn being worked on, live.
            working && (
              <AgentSteps
                executionId={conv.execution.id}
                turn={{ replies: messages.filter((m) => m.senderType === 'agent' && m.executionId === conv.execution?.id).length, open: true }}
                working
                defaultOpen
                agentName={agentName}
                className="ml-9 max-sm:ml-0"
              />
            )
          ) : conv?.execution && (
            <AgentSteps
              executionId={conv.execution.id}
              turn={currentTurn(messages, conv.execution.createdAt)}
              working={working}
              className="ml-9 max-sm:ml-0"
            />
          )}
          {footer}
        </div>
      </div>

      <div className="border-t bg-background">
        <div className="mx-auto flex max-w-3xl flex-col gap-2 px-3 pt-2.5 pb-[max(0.625rem,env(safe-area-inset-bottom))] sm:px-6 sm:py-3">
          {(openWorkItems.length > 0 || openRounds.length > 0) && (
            <WorkItemsTray ids={openWorkItems} rounds={openRounds} questions={talk.questions} activity={talk.activity} messages={messages} done={done} />
          )}
          {banner}
          {!disabledNotice && attachments && <AttachmentChips attachments={attachments} />}
          {disabledNotice ?? (
            // The field to write in (docs/features/conversations.md#the-field-to-write-in): one card, the text on top, its
            // buttons below; the round button on the right sends, or dictates while there is nothing to send.
            <div
              className={cn(
                'relative rounded-2xl border bg-card shadow-sm transition-[border-color,box-shadow] focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/20',
                dictation.listening && 'border-destructive/50 ring-2 ring-destructive/15',
              )}
            >
              {picker.menu}
              <Textarea
                ref={input.ref}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => picker.onKeyDown(e) || input.onKeyDown(e)}
                placeholder={input.placeholder(placeholder ?? t('Message {agent} … (Enter to send, Shift+Enter for a new line)', { agent: agentName }))}
                rows={1}
                enterKeyHint={input.touch ? 'enter' : 'send'}
                className="max-h-44 min-h-12 resize-none rounded-2xl border-0 bg-transparent px-4 pt-3 pb-1 shadow-none focus-visible:ring-0 sm:max-h-64 sm:text-[15px] dark:bg-transparent"
                aria-label={t('Message')}
                autoFocus={autoFocus}
              />
              <div className="flex items-center gap-1 px-2 pb-2">
                {attachments && <AttachButton attachments={attachments} className="size-9 rounded-full" />}
                {composerExtra}
                <span className="min-w-0 flex-1 truncate px-1 text-xs text-muted-foreground" aria-live="polite">
                  {dictation.busy ? t('Transcribing …') : dictation.listening ? t('Listening … tap the square when you are done') : null}
                </span>
                <ComposerButton canSend={canSend} dictation={dictation} onSend={submit} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
  if (!attachments) return thread;
  return (
    <DropZone onFiles={attachments.add} className={cn('flex min-h-0 flex-col', className)}>
      {thread}
    </DropZone>
  );
}

/**
 * The one round button of the field: Send while there is something to send, the microphone while there is not (and
 * the browser can dictate), stop while dictating. It switches by itself, as in chat apps.
 */
function ComposerButton({
  canSend,
  dictation,
  onSend,
}: {
  canSend: boolean;
  dictation: { supported: boolean; listening: boolean; busy: boolean; toggle: () => void };
  onSend: () => void;
}) {
  const round = 'size-10 shrink-0 rounded-full transition-transform active:scale-95 [&_svg]:size-5';
  if (dictation.busy) {
    return (
      <Button type="button" size="icon" disabled className={round} aria-label={t('Transcribing …')}>
        <Loader2 className="animate-spin" />
      </Button>
    );
  }
  if (dictation.listening) {
    return (
      <Button type="button" size="icon" variant="destructive" onClick={dictation.toggle} className={cn(round, 'animate-pulse')} aria-label={t('Stop dictation')} title={t('Stop dictation')}>
        <Square className="fill-current" />
      </Button>
    );
  }
  if (!canSend && dictation.supported) {
    return (
      <Button type="button" size="icon" variant="secondary" onClick={dictation.toggle} className={round} aria-label={t('Dictate')} title={t('Dictate')}>
        <Mic />
      </Button>
    );
  }
  return (
    <Button type="button" size="icon" onClick={onSend} disabled={!canSend} className={round} aria-label={t('Send message')} title={t('Send message')}>
      <ArrowUp />
    </Button>
  );
}

// ---------------------------------------------------------------- messages

function suggestionsOf(m: ConversationMessage): string[] {
  const list = (m.metadata as { suggestions?: unknown } | undefined)?.suggestions;
  return Array.isArray(list) ? list.filter((s): s is string => typeof s === 'string' && !!s.trim()).slice(0, 4) : [];
}

/**
 * Agent reply id → the answer the person picked last of those it offers: a message sent with `answers`, or (before
 * answers carried it) the person's next message when it reads as one of them.
 */
function chosenAnswers(messages: ConversationMessage[]): Map<string, string> {
  const chosen = new Map<string, string>();
  let asked: ConversationMessage | null = null;
  for (const m of messages) {
    if (m.senderType === 'system') continue;
    if (m.senderType === 'agent') {
      asked = suggestionsOf(m).length ? m : null;
      continue;
    }
    const answers = (m.metadata as { answers?: { messageId?: string } } | undefined)?.answers?.messageId;
    if (answers) chosen.set(answers, m.content);
    else if (asked && !chosen.has(asked.id) && suggestionsOf(asked).includes(m.content)) chosen.set(asked.id, m.content);
    asked = null;
  }
  return chosen;
}

export function isWorkItemMessage(m: ConversationMessage): boolean {
  return !!workItemMeta(m);
}

/** Agent reply id → which reply of its run it is (1-based); the k-th reply ends the run's k-th turn. */
function turnsOfReplies(messages: ConversationMessage[]): Map<string, number> {
  const perRun = new Map<string, number>();
  const turns = new Map<string, number>();
  for (const m of messages) {
    if (m.senderType !== 'agent' || !m.executionId) continue;
    const n = (perRun.get(m.executionId) ?? 0) + 1;
    perRun.set(m.executionId, n);
    turns.set(m.id, n);
  }
  return turns;
}

function MessageItem({
  message: m,
  agentName,
  workspaceId,
  deskId,
  workItem,
  questions,
  talkActivity,
  error,
  replyTurn,
  mark,
  choice,
  onPick,
}: {
  message: ConversationMessage;
  agentName: string;
  workspaceId: string;
  deskId: string | null;
  /** The Work Item this message is about, as the chat knows it. */
  workItem?: ChatWorkItem;
  /** The questions asked from this chat, live (a round's card shows its own). */
  questions?: Map<string, AskedQuestion>;
  talkActivity?: TalkActivity;
  error: boolean;
  /** Set where every reply shows its steps: which reply of its run this is. */
  replyTurn?: number;
  /** A person's message the agent at work has not answered yet: whether it read it. */
  mark?: Mark;
  /** Of the answers this reply offers, the one the person picked. */
  choice?: string;
  /** Sends an answer the reply offers; without it they only show what was picked. */
  onPick?: (message: ConversationMessage, answer: string) => void;
}) {
  if (m.senderType === 'system') {
    // A chat that writes (docs/features/conversations.md#writing-in-a-chat): approval cards and the files agents put here.
    const meta = m.metadata as { event?: string; actionId?: string; artifact?: Artifact } | undefined;
    // The training (docs/features/desk-training.md): the Lead's proposals to decide with one click, and its milestones.
    if (deskId && isTrainingProposal(m)) return <TrainingProposalCard message={m} deskId={deskId} />;
    if (isTrainingNotice(m)) return <TrainingNotice message={m} />;
    if (isHandoff(m)) return <HandoffCard message={m} workspaceId={workspaceId} />;
    if (meta?.event === 'action_requested' && meta.actionId) return <ChatAction conversationId={m.conversationId} actionId={meta.actionId} />;
    if (meta?.event === 'artifact' && meta.artifact) return <ChatFile artifact={meta.artifact} createdAt={m.createdAt} />;
    if (meta?.event === 'action_decided') return <DecisionNotice content={m.content} createdAt={m.createdAt} />;
    // VICKI's approval cards (docs/features/front-desk.md#approval-cards).
    const vicki = (m.metadata as { vickiActionId?: string } | undefined)?.vickiActionId;
    if (meta?.event === 'vicki_action_requested' && vicki) return <VickiActionCard conversationId={m.conversationId} actionId={vicki} />;
    if (meta?.event === 'vicki_action_decided') return <DecisionNotice content={m.content} createdAt={m.createdAt} />;
    // A question asked here and a desk's answer to it (docs/features/desk-talk.md).
    if (isTalkAsked(m)) return <TalkRoundCard message={m} questions={questions ?? new Map()} activity={talkActivity} />;
    if (isTalkNotice(m)) return <TalkNotice message={m} workspaceId={workspaceId} />;
    // What VICKI keeps at, her driver and her watches (docs/features/front-desk.md#driving-a-chat).
    if (isDriveNotice(m)) return <DriveNotice message={m} />;
    if (workItem) return <WorkItemCard message={m} item={workItem} workspaceId={workspaceId} />;
    return (
      <div className="flex justify-center">
        <div
          title={formatAbsolute(m.createdAt, true)}
          className={cn(
            'max-w-[90%] rounded-lg px-3 py-1.5 text-center text-xs break-words whitespace-pre-wrap',
            error
              ? 'border border-red-200 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200'
              : 'bg-muted text-muted-foreground',
          )}
        >
          {m.content}
        </div>
      </div>
    );
  }
  if (m.senderType === 'user') {
    const files = (m.metadata as { resources?: { id: string; name: string }[] } | undefined)?.resources;
    const asked = askedOf(m);
    return (
      <UserBubble content={m.content} createdAt={m.createdAt} name={m.senderName} asked={asked}>
        {files?.length ? <MessageAttachments resources={files} /> : null}
        {mark && <ReadMark mark={mark} agentName={agentName} />}
      </UserBubble>
    );
  }
  return (
    <div className="flex gap-2.5">
      {/* On a phone the reply takes the full width: the avatar moves into the name line. */}
      <div className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-indigo-500/15 text-indigo-600 max-sm:hidden dark:text-indigo-300">
        <Bot className="size-3.5" />
      </div>
      <div className="flex max-w-[85%] min-w-0 flex-col max-sm:max-w-full">
        <div className="mb-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
          <Bot className="size-3.5 text-indigo-600 sm:hidden dark:text-indigo-300" />
          <span className="font-medium text-foreground/80">{m.senderName || agentName}</span>
          {askedOf(m) && <AskedTo name={askedOf(m)!} />}
          <time dateTime={m.createdAt} title={formatAbsolute(m.createdAt, true)}>
            {formatTime(m.createdAt)}
          </time>
          <SessionLink executionId={m.executionId} workspaceId={workspaceId} />
        </div>
        {replyTurn && m.executionId && (
          <AgentSteps executionId={m.executionId} turn={{ replies: replyTurn, open: false }} working={false} defaultOpen className="mb-1.5" />
        )}
        <div className="rounded-2xl rounded-tl-sm border bg-card px-3.5 py-2.5">
          <Markdown text={m.content} />
          <SuggestedAnswers message={m} choice={choice} onPick={onPick} />
        </div>
      </div>
    </div>
  );
}

/** The answers a reply offers, inside it: one click sends one, the one picked stays marked and can be changed. */
function SuggestedAnswers({ message, choice, onPick }: { message: ConversationMessage; choice?: string; onPick?: (message: ConversationMessage, answer: string) => void }) {
  const answers = suggestionsOf(message);
  if (!answers.length) return null;
  return (
    <div className="mt-2.5 flex flex-wrap gap-1.5 border-t pt-2.5" role="group" aria-label={t('Suggested answers')}>
      {answers.map((a) => {
        const picked = a === choice;
        return (
          <button
            key={a}
            type="button"
            aria-pressed={picked}
            disabled={!onPick || picked}
            onClick={() => onPick?.(message, a)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-left text-sm transition-colors',
              picked
                ? 'border-primary bg-primary text-primary-foreground'
                : 'bg-background text-foreground enabled:cursor-pointer enabled:hover:bg-accent disabled:opacity-60',
              choice && !picked && 'text-muted-foreground',
            )}
          >
            {picked && <Check className="size-3.5 shrink-0" />}
            {a}
          </button>
        );
      })}
    </div>
  );
}

/** What became of a call a person decided on: the line, with the tool's result (often long JSON) only on click. */
function DecisionNotice({ content, createdAt }: { content: string; createdAt: string }) {
  const [open, setOpen] = useState(false);
  const split = content.indexOf('\n\n');
  const head = split < 0 ? content : content.slice(0, split);
  const details = split < 0 ? '' : content.slice(split + 2).replace(/^Result:\n/, '');
  return (
    <div className="flex justify-center">
      <div title={formatAbsolute(createdAt, true)} className="flex max-w-[90%] flex-col items-center gap-1 rounded-lg bg-muted px-3 py-1.5 text-xs text-muted-foreground">
        <Markdown text={head} className="gap-1 text-center text-xs" />
        {details && (
          <button type="button" onClick={() => setOpen((o) => !o)} className="cursor-pointer text-[11px] font-medium text-foreground/70 hover:underline">
            {open ? t('Hide details') : t('Show details')}
          </button>
        )}
        {open && <pre className="max-h-64 w-full overflow-auto rounded-md bg-background/60 p-2 text-left font-mono text-[11px] whitespace-pre-wrap">{details}</pre>}
      </div>
    </div>
  );
}

/** A file an agent put into this chat (docs/features/assets.md): a picture or PDF as a small preview, large on click. */
function ChatFile({ artifact, createdAt }: { artifact: Artifact; createdAt: string }) {
  return (
    <div className="ml-9 flex max-w-[85%] flex-col max-sm:ml-0 max-sm:max-w-full">
      <div className="mb-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
        <span className="truncate font-medium text-foreground/80">{artifact.filename}</span>
        <time dateTime={createdAt} title={formatAbsolute(createdAt, true)}>
          {formatTime(createdAt)}
        </time>
      </div>
      <FilePreview file={artifactFile(artifact)} />
    </div>
  );
}

export function UserBubble({
  content,
  createdAt,
  name,
  pending,
  asked,
  children,
}: {
  content: string;
  createdAt: string;
  name?: string | null;
  pending?: boolean;
  /** Whom the message asks (docs/features/desk-talk.md). */
  asked?: string | null;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-row-reverse gap-2.5">
      <div className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground max-sm:hidden">
        <User className="size-3.5" />
      </div>
      <div className="flex max-w-[80%] min-w-0 flex-col items-end max-sm:max-w-[88%]">
        <div className="mb-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className="font-medium text-foreground/80">{name || t('You')}</span>
          {asked && <AskedTo name={asked} />}
          <time dateTime={createdAt} title={formatAbsolute(createdAt, true)}>
            {pending ? t('sending …') : formatTime(createdAt)}
          </time>
        </div>
        <div
          className={cn(
            'rounded-2xl rounded-tr-sm bg-primary px-3.5 py-2 text-sm break-words whitespace-pre-wrap text-primary-foreground',
            pending && 'opacity-70',
          )}
        >
          {content}
        </div>
        {children}
      </div>
    </div>
  );
}

function AgentStatusLine({
  state,
  activity,
  agentName,
  failedText,
}: {
  state: ConversationAgentState;
  activity: string | null;
  agentName: string;
  failedText?: string;
}) {
  let icon: ReactNode;
  let text: ReactNode;
  let tone = 'text-muted-foreground';
  switch (state) {
    case 'starting':
      icon = <Loader2 className="size-3.5 animate-spin" />;
      text = t('{agent} is starting …', { agent: agentName });
      tone = 'text-sky-700 dark:text-sky-300';
      break;
    case 'thinking':
      icon = <Sparkles className="size-3.5 animate-soft-pulse" />;
      text = t('{agent} is thinking …', { agent: agentName });
      tone = 'text-indigo-700 dark:text-indigo-300';
      break;
    case 'listening':
      icon = <Ear className="size-3.5" />;
      text = t('{agent} is listening', { agent: agentName });
      break;
    case 'sleeping':
      icon = <Moon className="size-3.5" />;
      text = t('{agent} is asleep – your next message wakes it up (takes a few seconds)', { agent: agentName });
      break;
    case 'failed':
      icon = <AlertTriangle className="size-3.5" />;
      // The error itself is shown as the red system message right above — don't repeat it.
      text = failedText ? t('The last answer failed (see above).') : t('Something went wrong with the last answer.');
      tone = 'text-destructive';
      break;
  }
  const showActivity = activity && (state === 'starting' || state === 'thinking');
  return (
    <div className={cn('flex flex-col gap-0.5 pl-9 text-xs', tone)} aria-live="polite">
      <div className="flex items-center gap-1.5">
        {icon}
        <span className="break-words">{text}</span>
      </div>
      {showActivity && <div className="truncate pl-5 text-muted-foreground italic">{activity}</div>}
    </div>
  );
}
